import React, { useEffect, useState } from 'react';
import { 
  Theme, 
  ThemeContextType, 
  ThemeContext, 
  THEME_STORAGE_KEY 
} from './ThemeContext';

// ==============================================
// UTILITY FUNCTIONS
// ==============================================

/**
 * Detecta la preferencia del sistema
 */
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

/**
 * Obtiene el tema guardado en localStorage
 */
const getSavedTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      return saved as Theme;
    }
  } catch (error) {
    console.warn('Error al leer tema del localStorage:', error);
  }
  
  return 'system';
};

/**
 * Guarda el tema en localStorage
 */
const saveTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.warn('Error al guardar tema en localStorage:', error);
  }
};

/**
 * Aplica el tema al DOM
 */
const applyTheme = (theme: 'light' | 'dark'): void => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  const body = document.body;

  // Añadir clase temporal para deshabilitar transiciones
  root.classList.add('theme-transition-disable');
  body.classList.add('theme-transition-disable');
  
  // Aplicar el tema
  root.setAttribute('data-theme', theme);
  body.setAttribute('data-theme', theme);
  
  // Re-habilitar transiciones después de un breve delay
  setTimeout(() => {
    root.classList.remove('theme-transition-disable');
    body.classList.remove('theme-transition-disable');
  }, 50);
};

// ==============================================
// THEME PROVIDER COMPONENT
// ==============================================

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Tema por defecto si no hay preferencia guardada */
  defaultTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system'
}) => {
  const [themeMode, setThemeMode] = useState<Theme>(() => {
    const saved = getSavedTheme();
    return saved !== 'system' ? saved : defaultTheme;
  });

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => getSystemTheme());

  // Tema efectivo que se aplica
  const theme: 'light' | 'dark' = themeMode === 'system' ? systemTheme : themeMode;
  const isSystemTheme = themeMode === 'system';

  // Escuchar cambios en la preferencia del sistema
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Listener moderno
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Fallback para navegadores antiguos
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Aplicar tema al DOM cuando cambia
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Guardar preferencia cuando cambia
  useEffect(() => {
    saveTheme(themeMode);
  }, [themeMode]);

  // Funciones para cambiar tema
  const setTheme = (newTheme: Theme) => {
    setThemeMode(newTheme);
  };

  const toggleTheme = () => {
    if (themeMode === 'system') {
      // Si está en system, cambiar al opuesto del actual
      setThemeMode(systemTheme === 'dark' ? 'light' : 'dark');
    } else {
      // Si está en light/dark, alternar
      setThemeMode(themeMode === 'dark' ? 'light' : 'dark');
    }
  };

  const contextValue: ThemeContextType = {
    theme,
    themeMode,
    setTheme,
    toggleTheme,
    isSystemTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};



// ==============================================
// THEME GUARD COMPONENT
// ==============================================

/**
 * Componente que previene flash of unstyled content
 */
export const ThemeGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // En SSR, renderizar sin tema hasta que se monte
  if (!mounted) {
    return <div style={{ visibility: 'hidden' }}>{children}</div>;
  }

  return <>{children}</>;
};

export default ThemeProvider; 